import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/LanguageContext';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Rocket,
  Plus,
  User,
  LogOut,
  Settings,
  Shield,
  Menu,
  X,
  Zap,
  Rss,
  MessageCircle,
  Globe,
} from 'lucide-react';
import { getAvatarFallback } from '../lib/utils';
import { Toaster } from './ui/sonner';
import { NotificationBell } from './NotificationBell';

export function Layout({ children }) {
  const { user, isAuthenticated, logout, unreadMessages } = useAuth();
  const { t, language, changeLanguage } = useTranslation();
  const siteSettings = useSiteSettings();
  const siteName = siteSettings.site_name || 'Fikkr';
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const toggleLanguage = () => {
    changeLanguage(language === 'en' ? 'tr' : 'en');
  };

  const navLinks = [
    { href: '/discover', label: t('nav.discover'), icon: Rocket },
    { href: '/feed', label: t('nav.feed'), icon: Rss },
    { href: '/leaderboard', label: t('nav.leaderboard'), icon: Zap },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-card border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center gap-2 group"
              data-testid="logo-link"
            >
              {siteSettings.logo_url ? (
                <img src={siteSettings.logo_url} alt={siteName} className="h-9 object-contain" />
              ) : (
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow">
                  <Rocket className="w-5 h-5 text-white" />
                </div>
              )}
              <span className="text-xl font-bold text-slate-900 hidden sm:block">
                {siteName}
              </span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-6">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                    location.pathname === link.href
                      ? 'text-orange-600'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <link.icon className="w-4 h-4" />
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Actions */}
            <div className="flex items-center gap-2">
              {/* Language Toggle */}
              <button
                onClick={toggleLanguage}
                className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                title={language === 'en' ? 'Turkceye gecis' : 'Switch to English'}
              >
                <Globe className="w-4 h-4" />
                <span className="hidden sm:inline">{language === 'en' ? 'TR' : 'EN'}</span>
              </button>

              {isAuthenticated ? (
                <>
                  <Button
                    onClick={() => navigate('/submit')}
                    className="hidden sm:flex btn-boost text-sm"
                    data-testid="submit-project-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    {t('nav.submit')}
                  </Button>

                  {/* Messages */}
                  <button
                    onClick={() => navigate('/messages')}
                    className="relative p-2 text-slate-600 hover:text-slate-900 transition-colors"
                    data-testid="messages-btn"
                  >
                    <MessageCircle className="w-5 h-5" />
                    {unreadMessages > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 bg-orange-500 text-white text-[10px] font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1">
                        {unreadMessages > 99 ? '99+' : unreadMessages}
                      </span>
                    )}
                  </button>

                  {/* Notifications */}
                  <NotificationBell />

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="flex items-center gap-2 focus:outline-none"
                        data-testid="user-menu-trigger"
                      >
                        <Avatar className="w-9 h-9 border-2 border-slate-200 hover:border-orange-300 transition-colors">
                          <AvatarImage src={user?.picture} alt={user?.name} />
                          <AvatarFallback className="bg-orange-100 text-orange-700 font-medium">
                            {getAvatarFallback(user?.name)}
                          </AvatarFallback>
                        </Avatar>
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <div className="px-3 py-2">
                        <p className="font-medium text-slate-900">{user?.name}</p>
                        <p className="text-sm text-slate-500">{user?.email}</p>
                      </div>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => navigate(`/profile/${user?.user_id}`)}
                        data-testid="menu-profile"
                      >
                        <User className="w-4 h-4 mr-2" />
                        {t('nav.profile')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/messages')}>
                        <MessageCircle className="w-4 h-4 mr-2" />
                        {t('nav.messages')}
                        {unreadMessages > 0 && (
                          <span className="ml-auto bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                            {unreadMessages}
                          </span>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/settings/profile')}>
                        <Settings className="w-4 h-4 mr-2" />
                        {t('nav.editProfile')}
                      </DropdownMenuItem>
                      {user?.is_admin && (
                        <DropdownMenuItem
                          onClick={() => navigate('/admin')}
                          data-testid="menu-admin"
                        >
                          <Shield className="w-4 h-4 mr-2" />
                          {t('nav.admin')}
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={handleLogout}
                        className="text-red-600"
                        data-testid="menu-logout"
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        {t('nav.logout')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/login')}
                    className="text-slate-600 hover:text-slate-900"
                    data-testid="login-btn"
                  >
                    {t('nav.login')}
                  </Button>
                  <Button
                    onClick={() => navigate('/signup')}
                    className="btn-boost text-sm"
                    data-testid="signup-btn"
                  >
                    {t('nav.signup')}
                  </Button>
                </>
              )}

              {/* Mobile menu button */}
              <button
                className="md:hidden p-2 text-slate-600 hover:text-slate-900"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Nav */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-slate-200">
              <nav className="flex flex-col gap-2">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      location.pathname === link.href
                        ? 'bg-orange-50 text-orange-600'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <link.icon className="w-4 h-4" />
                    {link.label}
                  </Link>
                ))}
                {isAuthenticated && (
                  <>
                    <Link
                      to="/messages"
                      className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <MessageCircle className="w-4 h-4" />
                      {t('nav.messages')}
                      {unreadMessages > 0 && (
                        <span className="ml-auto bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                          {unreadMessages}
                        </span>
                      )}
                    </Link>
                    <Link
                      to="/submit"
                      className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-orange-600 bg-orange-50"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Plus className="w-4 h-4" />
                      {t('nav.submitProject')}
                    </Link>
                  </>
                )}
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <Link to="/" className="flex items-center gap-2 mb-4">
                {siteSettings.logo_url ? (
                  <img src={siteSettings.logo_url} alt={siteName} className="h-9 object-contain" />
                ) : (
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                    <Rocket className="w-5 h-5 text-white" />
                  </div>
                )}
                <span className="text-xl font-bold">{siteName}</span>
              </Link>
              <p className="text-slate-400 text-sm max-w-md">
                {t('footer.description')}
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4">{t('footer.platform')}</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><Link to="/discover" className="hover:text-white transition-colors">{t('nav.discover')}</Link></li>
                <li><Link to="/feed" className="hover:text-white transition-colors">{t('nav.feed')}</Link></li>
                <li><Link to="/leaderboard" className="hover:text-white transition-colors">{t('nav.leaderboard')}</Link></li>
                <li><Link to="/submit" className="hover:text-white transition-colors">{t('nav.submitProject')}</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">{t('footer.legal')}</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><Link to="/page/terms" className="hover:text-white transition-colors">{t('footer.terms')}</Link></li>
                <li><Link to="/page/privacy" className="hover:text-white transition-colors">{t('footer.privacy')}</Link></li>
                <li><Link to="/page/kvkk" className="hover:text-white transition-colors">{t('footer.kvkk')}</Link></li>
              </ul>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-slate-800">
            <p className="text-slate-500 text-xs text-center">
              {t('legal.disclaimer')}
            </p>
            <p className="text-slate-600 text-xs text-center mt-4">
              &copy; {new Date().getFullYear()} {siteName}. {t('footer.rights')}
            </p>
          </div>
        </div>
      </footer>

      <Toaster position="bottom-right" />
    </div>
  );
}
